// Per-language label maps for BirthDetailsForm. Kept as a plain record (not inline
// ternaries) so the component stays simple and adding a label never grows cyclomatic
// complexity. Hindi is native Hinglish (Roman) — the verified brand register (rule 21:
// single Indian noun + English action word; pure Devanagari compounds score 0) — matching
// the Hinglish error copy on the same entry surfaces (/ai-astrologer, /free-astrology-chat,
// /free-kundali) where the plan's mobile form-failure classes concentrate. Guarded by
// scripts/check_hindi_register.py (no Devanagari U+0900–U+097F in the hi block).

export const BIRTH_FORM_LABELS = {
  en: {
    yourName: "Your Name",
    enterFullName: "Enter your full name",
    namePlaceholderOptional: "Name (optional)",
    birthDate: "Birth Date",
    birthTime: "Birth Time",
    required: "(required)",
    optional: "(optional)",
    timeRequiredHint: "Ascendant changes every ~2 hours — accurate time is essential.",
    enterBirthTime: "Enter your birth time to continue with a full chart.",
    timeOptionalHint: "Skip if unknown. Lagna-based insights need exact time.",
    timeUnknownToggle: "Don't know your birth time?",
    timeUnknownExplainer:
      "No problem — leave it blank and we'll calculate from your birth date and place. Without the exact time we won't claim an exact Lagna, houses, divisional charts, or dasha timing, and the Moon can change rashi or nakshatra within a single day.",
    birthPlace: "Birth Place",
    changePlace: "Change",
    // Fits a 360 px phone input (~226 px of text); the longer example list
    // was cut to "…(e.g. Mu" (b-calc-07).
    placePlaceholder: "Type your city, e.g. Delhi",
    pickCityHint: "Pick a city from the suggestions for precise coordinates.",
    calculating: "Calculating...",
    yearTooEarly: "Year must be 1900 or later.",
    futureDate: "Birth date cannot be in the future.",
    invalidDate: "Please enter a valid birth date.",
    defaultSubmit: "Calculate",
    checkDetails: "Please check the birth details above and try again.",
    pickCityToContinue: "Pick your city from the suggestions to continue.",
    enterName: "Please enter your name.",
    enterBirthDate: "Please enter your birth date.",
    enterBirthPlace: "Please enter your birth city.",
    // No longer than pickCityHint: they share its reserved line(s) under the
    // field (BirthPlaceField), and calculator slots were measured with it.
    placeNoMatch: "No town found — try your district or nearest big town.",
    placeNoMatchDevanagari: "Hindi spelling not found — type in English, e.g. Sitapur.",
  },
  hi: {
    yourName: "Aapka naam",
    enterFullName: "Apna pura naam likhein",
    namePlaceholderOptional: "Naam (optional)",
    birthDate: "Janm tithi",
    birthTime: "Janm samay",
    required: "(zaroori)",
    optional: "(optional)",
    timeRequiredHint: "Lagna har ~2 ghante mein badalta hai — sahi samay zaroori hai.",
    enterBirthTime: "Poore chart ke liye apna janm samay likhein.",
    timeOptionalHint: "Pata na ho to skip karein. Lagna-based insight ke liye sahi samay chahiye.",
    timeUnknownToggle: "Janm samay nahi pata?",
    timeUnknownExplainer:
      "Koi baat nahi — khaali chhod dein, hum janm tithi aur sthal se calculate karenge. Exact janm samay ke bina exact Lagna, bhaav, divisional charts ya dasha timing claim nahi karenge, aur Chandra ek hi din mein rashi ya nakshatra badal sakta hai.",
    birthPlace: "Janm sthal",
    changePlace: "Badlein",
    placePlaceholder: "Sheher likhein, jaise Delhi",
    pickCityHint: "Sahi coordinates ke liye suggestions mein se sheher chunein.",
    calculating: "Calculate ho raha hai...",
    yearTooEarly: "Saal 1900 ya uske baad ka hona chahiye.",
    futureDate: "Janm tithi future ki nahi ho sakti.",
    invalidDate: "Kripya sahi janm tithi daalein.",
    defaultSubmit: "Calculate karein",
    checkDetails: "Upar diye birth details check karke dobara try karein.",
    pickCityToContinue: "Aage badhne ke liye suggestions mein se apna sheher chunein.",
    enterName: "Kripya apna naam likhein.",
    enterBirthDate: "Kripya apni janm tithi daalein.",
    enterBirthPlace: "Apna janm sthal likhein.",
    placeNoMatch: "Town nahi mila — district ya paas ka bada sheher likhein.",
    placeNoMatchDevanagari: "Hindi naam nahi mila — English mein likhein, jaise Sitapur.",
  },
} as const;

export function birthFormLabels(language: "en" | "hi") {
  return BIRTH_FORM_LABELS[language];
}
